import { useQuery, useMutation } from '@tanstack/react-query'
import { Checkbox, Form, InputNumber, Select, Button, Modal } from 'antd'
import FormSection from 'components/form-section'
import React, { useEffect, useRef, useState, useMemo } from 'react'
import { getKIcReferences, getFractureToughness } from 'service/completionOptimizer'
import { TableOutlined } from '@ant-design/icons'
import CustomizedTableEditor from './CustomizedTableEditor'
import { generateUuid } from 'utils/dataUtil'

const units = {
  youngs_module: {
    FIELD: 'psi',
    METRIC: 'GPa'
  },
  poisson_ratio: {
    FIELD: 'n/a',
    METRIC: 'n/a'
  },
  fracture_toughness: {
    FIELD: 'psi.in^0.5',
    METRIC: 'MPa∙m^0.5'
  },
  formation_thinkness: {
    FIELD: 'ft',
    METRIC: 'm'
  },
  stage_length: {
    FIELD: '',
    METRIC: '(m)'
  },
  number_cluster: {
    FIELD: 'n/a',
    METRIC: 'n/a'
  },
  perf_each_cluster: {
    FIELD: 'n/a',
    METRIC: 'n/a'
  },
  perf_diameter: {
    FIELD: 'inch',
    METRIC: 'cm'
  },
  perf_angle: {
    FIELD: '360',
    METRIC: '360'
  },
  leakOff_coe: {
    FIELD: 'ft/sec^0.5',
    METRIC: 'm/sec^0.5'
  },
  pumping_rate: {
    FIELD: 'bbl/min',
    METRIC: 'm^3/min'
  },
  fluid_viscosity: {
    FIELD: 'Pa∙sec',
    METRIC: 'Pa∙sec'
  },
  pumping_time: {
    FIELD: 'min',
    METRIC: 'min'
  },
  sand_usage: {
    FIELD: 'lb',
    METRIC: 'kg'
  }
}

const CustomizedSettings = ({ stageIndex }) => {
  const form = Form.useFormInstance()
  const simulationUnit = Form.useWatch(["GENERAL", "unit"])
  const kicReferenced = Form.useWatch(['Completion', 'hydraulic_fracture', stageIndex, 'CUSTOMIZED', 'geomechanics', 'k_ic_reference'])
  const [isTableEditorOpen, setIsTableEditorOpen] = useState(false)
  const [editingData, setEditingData] = useState([])
  const tableEditorRef = useRef(null)

  const { data: kicReferences } = useQuery({
    queryKey: ['getKIcReferences'],
    queryFn: () => getKIcReferences()
  })

  console.log('KIc References from API:', kicReferences)

  const { mutate: fetchFractureToughness } = useMutation({
    mutationFn: getFractureToughness,
    onSuccess: (response) => {
      console.log('API Response:', response);
      form.setFieldValue(
        ['Completion', 'hydraulic_fracture', stageIndex, 'CUSTOMIZED', 'geomechanics', 'fracture_toughness'],
        response.fracture_toughness
      )
    },
    onError: (error) => {
      console.error('API Error:', error);
    }
  })

  const handleKicNameChange = (value) => {
    const youngs_module = form.getFieldValue([
      'Completion', 
      'hydraulic_fracture', 
      stageIndex, 
      'CUSTOMIZED', 
      'geomechanics', 
      'youngs_module'
    ])

    console.log('Young\'s module:', youngs_module);
    
    if (youngs_module) {
      console.log('Calling API with:', {
        name: value,
        youngs_module: youngs_module
      });

      fetchFractureToughness({
        name: value,
        youngs_module: youngs_module
      })
    }
  }

  const tableColumns = useMemo(() => [
    {
      title: "Young's <br/> modulus (E)",
      field: "youngs_module",
      editable: true
    },
    {
      title: "Poisson <br/>ratio (ν)",
      field: "poisson_ratio",
      editable: true
    },
    {
      title: "Fracture <br/> toughness (KIc)",
      field: "fracture_toughness",
      editable: true
    },
    {
      title: "KIc <br/>reference",
      field: "k_ic_reference",
      formatter: "tickCross",
      editor: true,
      cellClick: function(e, cell) {
        const newValue = !cell.getValue();
        cell.setValue(newValue);
        
        // Clear kic_name when unchecked
        if (!newValue) {
          cell.getRow().update({ kic_name: '' });
        }
        
        // Force table redraw to update select visibility
        cell.getTable().redraw(true);
      },
      width: 80
    },
    {
      title: "KIc Name",
      field: "kic_name",
      editor: "select",
      editorParams: {
        values: kicReferences?.map(item => item.name) || [],
        elementAttributes: {
          style: "width: 100%"
        }
      },
      formatter: function(cell) {
        return cell.getRow().getData().k_ic_reference ? cell.getValue() : '';
      },
      width: 120
    },
    {
      title: "Formation <br/> thickness (D)",
      field: "formation_thinkness",
      editable: true
    },
    {
      title: "Stage length",
      field: "stage_length",
      editable: true
    },
    {
      title: "Number<br/>of<br/> cluster",
      field: "number_cluster",
      editable: true
    },
    {
      title: "Perf of <br/> each <br/> cluster",
      field: "perf_each_cluster",
      editable: true
    },
    {
      title: "Perf <br/>diameter",
      field: "perf_diameter",
      editable: true
    },
    {
      title: "Perf <br/>angle",
      field: "perf_angle",
      editable: true
    },
    {
      title: "LeakOff <br/>coefficient (CL)",
      field: "leakOff_coe",
      editable: true
    },
    {
      title: "Pumping <br/> rate (Q0)",
      field: "pumping_rate",
      editable: true
    },
    {
      title: "Fluid <br/>viscosity (μ)",
      field: "fluid_viscosity",
      editable: true
    },
    {
      title: "Pumping <br/>time (t)",
      field: "pumping_time",
      editable: true
    },
    {
      title: "Sand usage",
      field: "sand_usage",
      editable: true
    }
  ], [kicReferences]);

  console.log('KIc References:', kicReferences)
  console.log('Table Columns:', tableColumns)

  const handleTableEditorOpen = () => {
    const stageValues = form.getFieldValue(['Completion', 'hydraulic_fracture']) ?? []
    const tableData = stageValues.map((stage, idx) => ({
      key: generateUuid(),
      stageNumber: idx + 1,
      youngs_module: stage.CUSTOMIZED?.geomechanics?.youngs_module ?? '',
      poisson_ratio: stage.CUSTOMIZED?.geomechanics?.poisson_ratio ?? '',
      fracture_toughness: stage.CUSTOMIZED?.geomechanics?.fracture_toughness ?? '',
      formation_thinkness: stage.CUSTOMIZED?.geomechanics?.formation_thinkness ?? '',
      stage_length: stage.CUSTOMIZED?.custom_completion?.stage_length ?? '',
      number_cluster: stage.CUSTOMIZED?.custom_completion?.number_cluster ?? '',
      perf_each_cluster: stage.CUSTOMIZED?.custom_completion?.perf_each_cluster ?? '',
      perf_diameter: stage.CUSTOMIZED?.custom_completion?.perf_diameter ?? '',
      perf_angle: stage.CUSTOMIZED?.custom_completion?.perf_angle ?? '',
      leakOff_coe: stage.CUSTOMIZED?.treatemnt?.leakOff_coe ?? '',
      pumping_rate: stage.CUSTOMIZED?.treatemnt?.pumping_rate ?? '',
      fluid_viscosity: stage.CUSTOMIZED?.treatemnt?.fluid_viscosity ?? '',
      pumping_time: stage.CUSTOMIZED?.treatemnt?.pumping_time ?? '',
      sand_usage: stage.CUSTOMIZED?.treatemnt?.sand_usage ?? '',
      k_ic_reference: stage.CUSTOMIZED?.geomechanics?.k_ic_reference ?? false,
      kic_name: stage.CUSTOMIZED?.geomechanics?.kic_name ?? ''
    }))
    setEditingData(tableData)
    setIsTableEditorOpen(true)
  }

  const handleTableEditorCancel = () => {
    setIsTableEditorOpen(false)
  }

  const handleTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || []
    
    currentEditingData.forEach((row, index) => {
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'youngs_module'], row.youngs_module)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'poisson_ratio'], row.poisson_ratio)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'fracture_toughness'], row.fracture_toughness)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'formation_thinkness'], row.formation_thinkness)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'stage_length'], row.stage_length)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'number_cluster'], row.number_cluster)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'perf_each_cluster'], row.perf_each_cluster)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'perf_diameter'], row.perf_diameter)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'perf_angle'], row.perf_angle)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'leakOff_coe'], row.leakOff_coe)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'pumping_rate'], row.pumping_rate)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'fluid_viscosity'], row.fluid_viscosity)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'pumping_time'], row.pumping_time)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'sand_usage'], row.sand_usage)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'k_ic_reference'], row.k_ic_reference)
      form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'kic_name'], row.kic_name)
    })

    setIsTableEditorOpen(false)
  }

  return (
    <div className="flex flex-col gap-4">
      {/* <div className="flex justify-end mb-3">
        <Button 
          icon={<TableOutlined />}
          onClick={handleTableEditorOpen}
          title="Open Table Editor"
        />
      </div> */}
      <FormSection title="Geomechanics">
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'geomechanics', 'youngs_module']}
            label={`Young’s modulus (E) (${ units.youngs_module[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'geomechanics', 'poisson_ratio']}
            label={`Poisson ratio (ν) (${ units.poisson_ratio[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'geomechanics', 'fracture_toughness']}
            label={`Fracture toughness (KIc) (${ units.fracture_toughness[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'geomechanics', 'k_ic_reference']}
            valuePropName='checked'
            className="self-end"
          >
            <Checkbox className="w-full">KIc reference</Checkbox>
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'geomechanics', 'formation_thinkness']}
            label={`Formation thickness (D) (${ units.formation_thinkness[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
          {kicReferenced ? (
            <Form.Item
              name={[stageIndex, 'CUSTOMIZED', 'geomechanics', 'kic_name']}
              label={`Select KIc`}
            >
              <Select 
                options={kicReferences?.map(item => ({
                  value: item.name,
                  label: item.name
                }))} 
                onChange={handleKicNameChange}
              />
            </Form.Item>
          ) : null}
        </div>
      </FormSection>
      <FormSection title="Completion">
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'custom_completion', 'stage_length']}
            label={`Stage length ${ units.stage_length[simulationUnit] }`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'custom_completion', 'number_cluster']}
            label={`Number of cluster (${ units.number_cluster[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'custom_completion', 'perf_each_cluster']}
            label={`Perf of each cluster (${ units.perf_each_cluster[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'custom_completion', 'perf_diameter']}
            label={`Perf diameter (${ units.perf_diameter[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'custom_completion', 'perf_angle']}
            label={`Perf angle (${ units.perf_angle[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
      </FormSection>
      <FormSection title="Treatment">
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'treatemnt', 'leakOff_coe']}
            label={`LeakOff coefficient (CL) (${ units.leakOff_coe[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'treatemnt', 'pumping_rate']}
            label={`Pumping rate (Q0) (${ units.pumping_rate[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'treatemnt', 'fluid_viscosity']}
            label={`Fluid viscosity (μ) (${ units.fluid_viscosity[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'treatemnt', 'pumping_time']}
            label={`Pumping time (t) (${ units.pumping_time[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name={[stageIndex, 'CUSTOMIZED', 'treatemnt', 'sand_usage']}
            label={`Sand usage (${ units.sand_usage[simulationUnit] })`}
          >
            <InputNumber className="w-full" />
          </Form.Item>
        </div>
      </FormSection>
      <Modal
        title="Stage Settings Editor"
        open={isTableEditorOpen}
        onCancel={handleTableEditorCancel}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          <CustomizedTableEditor
            ref={tableEditorRef}
            data={editingData}
            columns={tableColumns}
            onDataChange={setEditingData}
          />
        </div>
      </Modal>
    </div>
  )
}

export default CustomizedSettings